export function getFacilityPlanType(facility) {
  return facility?.facility_plan_type ?? facility?.plan_type ?? '';
}

export function getFacilityTypeName(facility) {
  return facility?.facility_type ?? facility?.type ?? '';
}

/**
 * Resolve Ant Design tier Select value from facility record + tier catalog.
 * Returns `null` only when plan is explicitly "all plans"; `undefined` when unknown (do not default to All Plans).
 */
export function resolveFacilityTierSelectValue(record, tierOptions = []) {
  if (record?.facility_tier_id != null && record.facility_tier_id !== '') {
    return Number(record.facility_tier_id);
  }
  const plan = String(getFacilityPlanType(record) || '').trim().toLowerCase();
  if (!plan) return undefined;
  if (plan === 'all plans') return null;
  const match = tierOptions.find(
    (tier) => String(tier?.tier_name || '').toLowerCase() === plan,
  );
  return match?.value ?? undefined;
}

/** Map v2 admin facility record → legacy provider shape used by FacilityData / updateProvider. */
/**
 * Resolve the Ant Design Select value for a facility's type from the
 * `facility_types` catalog.
 *
 * Prefers the stored id. Older records only carry the denormalized name, so
 * fall back to matching that against the catalog. When neither resolves — a
 * legacy value with no matching row, e.g. one written by the form's old
 * hardcoded list — return the raw string so the box shows what is actually
 * stored rather than looking unset.
 */
export function resolveFacilityTypeSelectValue(record, typeOptions = []) {
  const id = record?.facility_type_id;
  if (id != null && id !== '' && Number.isFinite(Number(id))) return Number(id);

  const raw = getFacilityTypeName(record);
  if (!raw) return undefined;

  const normalized = String(raw).trim().toLowerCase();
  const match = typeOptions.find(
    (opt) => String(opt?.label ?? '').trim().toLowerCase() === normalized,
  );
  return match?.value ?? raw;
}

export function toFacilityProviderRecord(facility, facilityId) {
  if (!facility) return {};
  const id = facilityId ?? facility.id ?? facility.facility_id;
  const status = facility.facility_status ?? facility.status;
  return {
    ...facility,
    facility_id: id,
    facility_name: facility.facility_name ?? facility.name ?? '',
    facility_code: facility.facility_code ?? facility.code ?? '',
    facility_status: status === 'active' ? 1 : Number(status ?? 0),
    facility_phone: facility.facility_phone ?? facility.phone ?? facility.contact_phone ?? '',
    facility_email: facility.facility_email ?? facility.email ?? facility.contact_email ?? '',
    facility_address: facility.facility_address ?? facility.address ?? '',
    facility_city: facility.facility_city ?? facility.city ?? '',
    facility_state: facility.facility_state ?? facility.state ?? '',
    facility_type: getFacilityTypeName(facility),
    facility_plan_type: getFacilityPlanType(facility),
    facility_tier_id: facility.facility_tier_id ?? facility.tier_id ?? null,
    facility_designation: facility.facility_designation ?? facility.contact_person ?? '',
    facility_website: facility.facility_website ?? facility.website ?? '',
    facility_date: facility.facility_date ?? facility.created_at ?? facility.createdAt ?? '',
    facility_staff_id: facility.facility_staff_id ?? facility.onboarded_by ?? '',
    contact_phone: facility.contact_phone ?? facility.facility_phone ?? facility.phone ?? '',
    contact_email: facility.contact_email ?? facility.facility_email ?? facility.email ?? '',
    users_count: facility.users_count,
    tariffs_count: facility.tariffs_count,
    appointments_count: facility.appointments_count,
    claims_count: facility.claims_count,
    updated_at: facility.updated_at,
  };
}

export function isFacilityActive(facility) {
  const status = facility?.facility_status ?? facility?.status;
  return status === 1 || status === '1' || status === 'active';
}

/**
 * Public directory visibility (website / client provider directory), independent
 * of facility_status (operational: portal, claims, check-in). Defaults to shown —
 * matches the backend column default and FacilityPresentationService's
 * COALESCE(facility_listed, 1) = 1 filter, so an unset value never reads as hidden.
 */
export function isFacilityListed(facility) {
  const listed = facility?.listed ?? facility?.facility_listed ?? facility?.show_in_directory;
  return listed === undefined || listed === null || listed === 1 || listed === '1' || listed === true;
}

/** Canonical facility_types labels (must match API FacilityBulkUploadService). */
export const CANONICAL_FACILITY_TYPES = [
  'Hospital',
  'Dental Clinic',
  'Eye Clinic',
  'Pharmacy',
  'Diagnostic Centre',
  'Dermatology Clinic',
  'Laboratory',
  'Gym & Spa centre',
];

/** Common sheet labels → canonical (server is authoritative; FE uses this for preview help). */
export const FACILITY_TYPE_SYNONYMS = {
  hospital: 'Hospital',
  'specialist hospital': 'Hospital',
  'general hospital': 'Hospital',
  'private hospital': 'Hospital',
  'teaching hospital': 'Hospital',
  'medical centre': 'Hospital',
  'medical center': 'Hospital',
  'medical clinic': 'Hospital',
  'eye care': 'Eye Clinic',
  'eye clinic': 'Eye Clinic',
  ophthalmic: 'Eye Clinic',
  ophthalmology: 'Eye Clinic',
  optometry: 'Eye Clinic',
  optical: 'Eye Clinic',
  dental: 'Dental Clinic',
  dentistry: 'Dental Clinic',
  'dental clinic': 'Dental Clinic',
  pharmacy: 'Pharmacy',
  chemist: 'Pharmacy',
  drugstore: 'Pharmacy',
  'drug store': 'Pharmacy',
  'diagnostic centre': 'Diagnostic Centre',
  'diagnostic center': 'Diagnostic Centre',
  diagnostics: 'Diagnostic Centre',
  diagnostic: 'Diagnostic Centre',
  laboratory: 'Laboratory',
  lab: 'Laboratory',
  pathology: 'Laboratory',
  'dermatology clinic': 'Dermatology Clinic',
  dermatology: 'Dermatology Clinic',
  'skin clinic': 'Dermatology Clinic',
  'gym & spa centre': 'Gym & Spa centre',
  'gym and spa centre': 'Gym & Spa centre',
  'gym & spa': 'Gym & Spa centre',
  'gym and spa': 'Gym & Spa centre',
  fitness: 'Gym & Spa centre',
  gym: 'Gym & Spa centre',
  spa: 'Gym & Spa centre',
  'keep fit': 'Gym & Spa centre',
  keepfit: 'Gym & Spa centre',
};

export function normalizeFacilityTypeKey(value = '') {
  return String(value || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Map a free-text type label to canonical when known; otherwise null. */
export function mapFacilityTypeLabel(raw) {
  const original = String(raw ?? '')
    .replace(/^\uFEFF/, '')
    .trim()
    .replace(/^[\s"'“”‘’«»]+|[\s"'“”‘’«»]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!original) return { canonical: null, matched: false, original: '' };
  const key = normalizeFacilityTypeKey(original);
  const fromSyn = FACILITY_TYPE_SYNONYMS[key];
  if (fromSyn) return { canonical: fromSyn, matched: true, original };
  const exact = CANONICAL_FACILITY_TYPES.find(
    (t) => normalizeFacilityTypeKey(t) === key,
  );
  if (exact) return { canonical: exact, matched: true, original };
  return { canonical: null, matched: false, original };
}

export function cleanFacilityBulkString(value = '') {
  return String(value ?? '')
    .replace(/^\uFEFF/, '')
    .trim()
    .replace(/^[\s"'“”‘’«»]+|[\s"'“”‘’«»]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Template headers for V2 bulk create (`POST /v2/admin/facilities/bulk`). Prefer TSV for addresses with commas. */
export const PROVIDERS_BATCH_CSV_HEADERS = [
  'name',
  'address',
  'city',
  'state',
  'email',
  'phone',
  'facility_tier_id',
  'facility_type_id',
];

/** UI copy for facility portal onboarding_status codes. */
export const FACILITY_ONBOARDING_STATUS_META = {
  sent: { label: 'Welcome email sent', color: 'success' },
  skipped_gym_spa: { label: 'Gym/Spa — no portal invite', color: 'default' },
  // A branch has no admin login of its own; it is administered through staff
  // accounts created for it from its main facility.
  skipped_branch: { label: 'Branch — managed from the main facility', color: 'processing' },
  skipped_no_email: { label: 'No email — add email then onboard', color: 'warning' },
  failed_email: { label: 'Password set; email failed — retry with force', color: 'error' },
  already_has_password: { label: 'Already has password — use force to regenerate', color: 'processing' },
  failed: { label: 'Onboarding failed', color: 'error' },
};

export function getFacilityOnboardingStatusMeta(status) {
  const key = String(status || '').trim();
  return FACILITY_ONBOARDING_STATUS_META[key] || (key
    ? { label: key, color: 'default' }
    : { label: '—', color: 'default' });
}

function detectCsvDelimiter(headerLine = '') {
  const tabs = (headerLine.match(/\t/g) || []).length;
  const pipes = (headerLine.match(/\|/g) || []).length;
  const commas = (headerLine.match(/,/g) || []).length;
  if (tabs >= pipes && tabs > commas) return '\t';
  if (pipes >= commas) return '|';
  return ',';
}

/**
 * RFC4180-ish CSV/TSV line splitter — respects quoted fields so commas inside
 * `"12 Main St, Lagos"` do not create extra columns.
 */
export function splitDelimitedLine(line = '', delimiter = ',') {
  const cells = [];
  let cur = '';
  let inQuotes = false;
  const s = String(line ?? '');
  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];
    if (inQuotes) {
      if (ch === '"') {
        if (s[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      continue;
    }
    if (ch === delimiter) {
      cells.push(cur);
      cur = '';
      continue;
    }
    cur += ch;
  }
  cells.push(cur);
  return cells;
}

function normalizeBulkHeader(raw = '') {
  const h = String(raw || '').trim().toLowerCase().replace(/^\ufeff/, '');
  const aliases = {
    facility_name: 'name',
    facility_address: 'address',
    facility_city: 'city',
    facility_state: 'state',
    facility_email: 'email',
    contact_email: 'email',
    facility_phone: 'phone',
    contact_phone: 'phone',
    tier: 'facility_tier_id',
    facility_tier: 'facility_tier_id',
    facility_type: 'facility_type_id',
    type: 'facility_type_id',
  };
  return aliases[h] || h;
}

function matrixToFacilityRows(matrix = []) {
  if (!Array.isArray(matrix) || matrix.length < 2) {
    return { rows: [], error: 'File must include a header row and at least one data row' };
  }
  const headers = (matrix[0] || []).map(normalizeBulkHeader);
  const required = ['name', 'address', 'city', 'state'];
  const missingRequired = required.filter((key) => !headers.includes(key));
  if (missingRequired.length) {
    return {
      rows: [],
      error: `Missing required columns: ${missingRequired.join(', ')}`,
    };
  }

  const rows = [];
  for (let i = 1; i < matrix.length; i += 1) {
    const cells = matrix[i] || [];
    const raw = {};
    headers.forEach((key, idx) => {
      if (!key) return;
      raw[key] = cleanFacilityBulkString(cells[idx] ?? '');
    });

    const name = raw.name;
    const address = raw.address;
    const city = raw.city;
    const state = raw.state;
    if (!name && !address && !city && !state) continue;

    const row = { name, address, city, state };
    if (raw.email) row.email = raw.email;
    if (raw.phone) row.phone = raw.phone;

    const tierRaw = raw.facility_tier_id;
    if (tierRaw !== '' && tierRaw != null) {
      const tierId = Number(tierRaw);
      if (Number.isFinite(tierId)) row.facility_tier_id = tierId;
    }

    const typeRaw = raw.facility_type_id;
    if (typeRaw !== '' && typeRaw != null) {
      const typeId = Number(typeRaw);
      if (Number.isFinite(typeId) && String(typeRaw).trim() !== '' && !Number.isNaN(typeId) && /^\d+$/.test(String(typeRaw).trim())) {
        row.facility_type_id = typeId;
      } else {
        const mapped = mapFacilityTypeLabel(typeRaw);
        row.facility_type = mapped.matched ? mapped.canonical : mapped.original;
        if (mapped.matched && mapped.canonical !== mapped.original) {
          row.normalized_type = `${mapped.original} → ${mapped.canonical}`;
        }
      }
    }

    rows.push(row);
  }

  if (!rows.length) {
    return { rows: [], error: 'No data rows found in file' };
  }
  if (rows.length > 500) {
    return { rows: [], error: 'Bulk upload supports at most 500 facilities per request' };
  }
  return { rows, error: null };
}

/**
 * Parse pipe-, comma-, or tab-delimited facility bulk text into API row objects.
 * Respects RFC4180 quoted fields. Skips blank lines. Does not invent passwords.
 */
export function parseFacilityBulkCsv(text = '') {
  const normalized = String(text || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n');
  const lines = normalized
    .split('\n')
    .map((line) => line.trimEnd())
    .filter((line) => line.trim() !== '');

  if (lines.length < 2) {
    return { rows: [], error: 'CSV must include a header row and at least one data row' };
  }

  const delimiter = detectCsvDelimiter(lines[0]);
  const matrix = lines.map((line) => splitDelimitedLine(line, delimiter).map((c) => c.trim()));
  return matrixToFacilityRows(matrix);
}

/**
 * Parse .csv / .tsv / .xlsx / .xls for facility bulk upload.
 * Excel uses the existing `xlsx` dependency.
 */
export async function parseFacilityBulkFile(file) {
  if (!file) {
    return { rows: [], error: 'No file selected', format: null };
  }
  const name = String(file.name || '').toLowerCase();
  const mime = String(file.type || '').toLowerCase();

  if (name.endsWith('.xlsx') || name.endsWith('.xls') || mime.includes('spreadsheet') || mime.includes('excel')) {
    try {
      const XLSX = await import('xlsx');
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const sheetName = wb.SheetNames?.[0];
      if (!sheetName) {
        return { rows: [], error: 'Excel workbook has no sheets', format: 'xlsx' };
      }
      const sheet = wb.Sheets[sheetName];
      const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false });
      const cleaned = (matrix || [])
        .map((row) => (Array.isArray(row) ? row.map((c) => String(c ?? '').trim()) : []))
        .filter((row) => row.some((c) => c !== ''));
      const parsed = matrixToFacilityRows(cleaned);
      return { ...parsed, format: name.endsWith('.xls') ? 'xls' : 'xlsx' };
    } catch (e) {
      return { rows: [], error: e?.message || 'Unable to parse Excel file', format: 'xlsx' };
    }
  }

  const text = await file.text();
  const format = name.endsWith('.tsv') || mime === 'text/tab-separated-values' ? 'tsv' : 'csv';
  const parsed = parseFacilityBulkCsv(text);
  return { ...parsed, format };
}

/** Normalize bulk API response into a flat list for the results table. */
export function normalizeFacilityBulkResults(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  const created = Array.isArray(data.created) ? data.created : [];
  const skipped = Array.isArray(data.skipped) ? data.skipped : [];
  const failed = Array.isArray(data.failed) ? data.failed : [];
  const results = Array.isArray(data.results) ? data.results : [];
  if (created.length || skipped.length || failed.length || results.length) {
    return [
      ...created.map((r) => ({ ...r, _result: 'created' })),
      ...skipped.map((r) => ({ ...r, _result: 'skipped' })),
      ...failed.map((r) => ({ ...r, _result: 'failed' })),
      ...results,
    ];
  }
  return [];
}

/** Build a client-side CSV report from the last bulk response (fallback when job_id missing). */
export function facilityBulkResultsToCsvRows(data) {
  const flat = normalizeFacilityBulkResults(data);
  return flat.map((row) => {
    const status = row._result
      || (row.facility_id != null || row.activated === true ? 'created' : null)
      || (row.skipped ? 'skipped' : null)
      || (row.error || row.errors ? 'failed' : 'unknown');
    const reasons = row.reason
      || (Array.isArray(row.reasons) ? row.reasons.join('; ') : row.reasons)
      || (Array.isArray(row.errors) ? row.errors.join('; ') : row.errors)
      || row.message
      || '';
    return {
      row: row.row ?? '',
      name: row.name || row.facility_name || '',
      status,
      facility_id: row.facility_id ?? '',
      onboarding_status: row.onboarding_status || row.onboarding?.onboarding_status || '',
      reasons,
      email_sent: row.email_sent === true ? 'yes' : (row.email_sent === false ? 'no' : ''),
    };
  });
}

/** The headline counts the facility detail page shows as metric tiles. */
export const FACILITY_COUNT_KEYS = [
  'users_count',
  'tariffs_count',
  'appointments_count',
  'claims_count',
];

/**
 * Fold a freshly fetched facility over the one already on screen, keeping the
 * headline counts when the new payload does not carry them.
 *
 * Stepping through the directory seeds the page from the carried list row,
 * which has all four counts, then replaces it with the detail response. A
 * detail payload missing those fields would blank them on that swap, which is
 * what made the metrics band flash up and then disappear mid-navigation.
 *
 * Only folds when both records are the same facility — across a step the
 * previous record belongs to a different one and must not leak into it.
 */
export function mergeFacilityCounts(prev, next, facilityId) {
  if (!next) return next;

  const prevId = prev?.id ?? prev?.facility_id;
  if (prevId == null || String(prevId) !== String(facilityId)) return next;

  const merged = { ...next };
  FACILITY_COUNT_KEYS.forEach((key) => {
    if (merged[key] == null && prev[key] != null) merged[key] = prev[key];
  });
  return merged;
}
